import {
  Image,
  Flex,
  Tag,
  Progress,
  Text,
  Box,
  Switch,
} from "@chakra-ui/react";
import { Dropdown, Menu } from "antd";
import CardDefault from "ui/components/Cards/CardDefault";
import { AiOutlineBank, AiOutlineBarcode } from "react-icons/ai";
import { FaEllipsisV } from "react-icons/fa";
import {
  TenantContaResponse,
  TipoTenantContaEnum,
} from "core/features/contas_bancarias/typings";
import useDeleteContaBancaria from "core/features/contas_bancarias/hooks/useDeleteContaBancaria";
import useToggleStatusContaBancaria from "core/features/contas_bancarias/hooks/useToggleStatusContaBancaria";
import useSaveBankAccount from "core/features/condominio/hooks/useSaveBankAccount";
import { showErrorAxios, showConfirm } from "infra/helpers/alerta";
import { useState } from "react";
import { useLoading } from "components/GlobalLoading";
import { formatMoney } from "infra/helpers/number";

export default function ContaItem({
  conta,
  tenant,
  onRefresh,
  onEdit,
  onOpenHistorico,
}: {
  conta: TenantContaResponse;
  tenant: string;
  onRefresh: () => void;
  onEdit: () => void;
  onOpenHistorico?: (contaId: string) => void;
}) {
  const [containerRef, setContainerRef] = useState<any>();

  const { mutate: saveAccount, isLoading: isLoadingSaveBankAccount } =
    useSaveBankAccount(tenant);

  const { mutate: handleDelete, isLoading: isLoadingDelete } =
    useDeleteContaBancaria(tenant);

  const { mutate: toggleStatus, isLoading: isLoadingToggleStatus } =
    useToggleStatusContaBancaria(tenant);

  const onError = (err: unknown) => {
    showErrorAxios(err);
  };

  const onUpdateField = (name: string, value: any, id: string) => {
    const _conta = conta;

    saveAccount(
      { ..._conta, id, [name]: value },
      {
        onSuccess: onRefresh,
        onError,
      }
    );
  };

  const onToggleStatus = () => {
    toggleStatus(conta.id, {
      onError,
      onSuccess: onRefresh,
    });
  };

  const menu = (
    <Menu>
      <Menu.Item onClick={() => onEdit()}>
        {conta.desabilitadaAdmin ? "Visualizar" : "Editar"}
      </Menu.Item>
      {onOpenHistorico ? (
        <Menu.Item onClick={() => onOpenHistorico(conta.id)}>
          Histórico
        </Menu.Item>
      ) : null}
      {!conta.desabilitadaAdmin ? (
        <Menu.Item
          onClick={() =>
            showConfirm().then(({ isConfirmed }) => {
              if (isConfirmed) {
                handleDelete(conta.id, {
                  onSuccess: () => {
                    onRefresh();
                  },
                  onError: (err) => {
                    showErrorAxios(err);
                  },
                });
              }
            })
          }
        >
          Excluir
        </Menu.Item>
      ) : null}
      {!conta.padrao && !conta.desabilitadaAdmin ? (
        <Menu.Item
          onClick={() => {
            onUpdateField("padrao", true, conta.id);
          }}
        >
          Tornar Padrão
        </Menu.Item>
      ) : null}
    </Menu>
  );

  useLoading(isLoadingToggleStatus);

  const isContaBancaria = conta.tipo === TipoTenantContaEnum.ContaCorrente;

  return (
    <CardDefault p="12px">
      {isLoadingSaveBankAccount || isLoadingDelete ? (
        <Progress
          size="sm"
          isIndeterminate
          w="100%"
          mb={4}
          colorScheme="cyan"
        />
      ) : null}
      <Flex gap="8px" ref={setContainerRef}>
        <Flex>
          {conta.imagemUrl ? (
            <Image rounded="6px" w="35px" h="35px" src={conta.imagemUrl} />
          ) : (
            <AiOutlineBank />
          )}
        </Flex>
        <Flex flex={1} flexDirection="column">
          <Box
            w={`calc(${(containerRef?.clientWidth ?? 0) - 70}px)`}
            title={conta.nome}
          >
            <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
              {conta.nome}
            </Text>
          </Box>
          {isContaBancaria ? (
            <Text fontSize="10px">Banco: {conta.banco.nome}</Text>
          ) : null}
        </Flex>

        <Dropdown trigger={["click"]} overlay={menu} arrow>
          <Flex cursor="pointer" alignItems="center">
            <FaEllipsisV color="#555" />
          </Flex>
        </Dropdown>
      </Flex>

      <Flex mt="16px" gap="8px" flexWrap="wrap">
        {conta.padrao ? (
          <Tag variant="outline" fontSize="12px" h="25px" colorScheme="blue">
            Padrão
          </Tag>
        ) : null}
        {conta.desabilitadaAdmin ? (
          <Tag variant="outline" fontSize="12px" h="25px" colorScheme="red">
            Desabilitada
          </Tag>
        ) : null}
        {conta.ativarPagamentoOnline && !conta.desabilitadaAdmin ? (
          <Tag
            gap="4px"
            h="25px"
            fontSize="12px"
            variant="outline"
            colorScheme="green"
            minW="200px"
          >
            Permite pagamento online
          </Tag>
        ) : null}
        {conta.emiteBoleto && !conta.desabilitadaAdmin ? (
          <Tag
            gap="4px"
            h="25px"
            fontSize="12px"
            variant="outline"
            colorScheme="blue"
          >
            <AiOutlineBarcode /> Emite Boleto
          </Tag>
        ) : null}
        {!conta.padrao && !conta.desabilitadaAdmin ? (
          <Flex ml="auto" alignSelf="center">
            <Switch
              onChange={onToggleStatus}
              isChecked={!!conta.ativo}
              isDisabled={isLoadingToggleStatus}
            />
          </Flex>
        ) : null}
      </Flex>

      {isContaBancaria ? (
        <Flex mt="16px" flexDir="column" gap="8px">
          <Flex>
            <Flex color="gray.500">Agência</Flex>
            <Flex flex={1} justifyContent="flex-end">
              {conta.agencia}
              {conta.agenciaDigito ? `-${conta.agenciaDigito}` : ""}
            </Flex>
          </Flex>

          <Flex>
            <Flex color="gray.500">Conta</Flex>
            <Flex flex={1} justifyContent="flex-end">
              {conta.numero}
              {conta.contaDigito ? `-${conta.contaDigito}` : ""}
            </Flex>
          </Flex>
        </Flex>
      ) : null}

      <Flex mx="-12px" px="12px" pt="8px" mt="16px" borderTop="1px solid #eee">
        <Flex color="gray.500">Saldo</Flex>
        <Flex
          flex={1}
          color={
            conta.saldo < 0 ? "red" : conta.saldo > 0 ? "green" : "gray.700"
          }
          justifyContent="flex-end"
        >
          {formatMoney(Number(conta.saldo))}
        </Flex>
      </Flex>
    </CardDefault>
  );
}
